import { ArrowUp } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FadeIn } from './motion-kit';
import { goToSection } from '../lib/scroll';
import { useI18n } from '../i18n';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { SERVICES } from '../content/services';
import { CITIES } from '../content/cities';

const SOCIALS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/codexalgerie' },
  { label: 'Instagram', href: 'https://www.instagram.com/codex_algerie/' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UC6K-49NQ63FyO0dV72jjZDA' },
  { label: 'Facebook', href: 'https://www.facebook.com/CodexAlgerie' },
  { label: 'WhatsApp', href: 'https://wa.me/213550357736' },
];

export function Footer() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lp = useLocalizedPath();
  const year = new Date().getFullYear();

  const nav = [
    { label: t.nav.about, id: 'about', kind: 'section' as const },
    { label: t.nav.services, id: 'services', kind: 'section' as const },
    { label: t.nav.work, to: lp('/projects'), kind: 'route' as const },
    { label: t.nav.blog, to: lp('/blog'), kind: 'route' as const },
    { label: t.nav.startProject, to: lp('/devis'), kind: 'route' as const },
    { label: t.nav.contact, id: 'contact', kind: 'section' as const },
  ];

  const serviceLinks = SERVICES.slice(0, 6).map((s) => ({
    label: s.locales[locale].h1,
    to: lp(`/services/${s.slug}`),
  }));

  const cityLinks = CITIES.map((c) => ({
    label: c.locales[locale].h1,
    to: lp(`/villes/${c.slug}`),
  }));

  const legal = [
    { label: t.legal.terms, to: lp('/terms') },
    { label: t.legal.privacy, to: lp('/privacy') },
  ];

  return (
    <footer
      className="relative px-6 md:px-12 pb-8 pt-10 hairline-t"
      style={{ background: 'var(--bg)' }}
    >
      <FadeIn>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          <div className="col-span-2">
            <Link to={lp('/')} className="inline-flex items-center gap-4" data-cursor>
              <img
                src="/brand/codex-mark.png"
                alt=""
                width={72}
                height={72}
                decoding="async"
                style={{
                width: 'clamp(2.5rem, 6vw, 4rem)',
                height: 'clamp(2.5rem, 6vw, 4rem)',
                objectFit: 'contain',
                borderRadius: 12,
                flex: 'none',
              }}
              />
              <span className="display" style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', lineHeight: 1 }}>
                Code<span style={{ color: 'var(--accent)' }}>x</span>
                <span style={{ color: 'var(--accent)' }}>.</span>
              </span>
            </Link>
            <p
              className="mt-6"
              style={{ color: 'var(--faint)', maxWidth: 320, fontSize: '0.875rem', lineHeight: 1.7 }}
            >
              {t.footer.blurb}
            </p>
          </div>

          <div>
            <div className="mono-label mb-6">{t.footer.sitemap}</div>
            <ul className="flex flex-col gap-3">
              {nav.map((n) => (
                <li key={n.label}>
                  {n.kind === 'route' ? (
                    <Link
                      to={n.to}
                      className="link-line"
                      style={{ color: 'var(--muted)', fontSize: '0.9rem' }}
                      data-cursor
                    >
                      {n.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => goToSection(n.id!, navigate, pathname)}
                      className="link-line"
                      style={{ color: 'var(--muted)', fontSize: '0.9rem' }}
                    >
                      {n.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mono-label mb-6">{t.footer.elsewhere}</div>
            <ul className="flex flex-col gap-3">
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-line"
                    style={{ color: 'var(--muted)', fontSize: '0.9rem' }}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pb-14">
          <div>
            <div className="mono-label mb-6">{t.nav.services}</div>
            <ul className="flex flex-col gap-3">
              {serviceLinks.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="link-line"
                    style={{ color: 'var(--muted)', fontSize: '0.85rem' }}
                    data-cursor
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="mono-label mb-6">
              {locale === 'fr' ? 'Villes' : locale === 'ar' ? 'مدن' : 'Cities'}
            </div>
            <ul className="flex flex-col gap-3">
              {cityLinks.map((c) => (
                <li key={c.to}>
                  <Link
                    to={c.to}
                    className="link-line"
                    style={{ color: 'var(--muted)', fontSize: '0.85rem' }}
                    data-cursor
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </FadeIn>

      <div className="hairline-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6">
        <span className="mono-label">© {year} Codex — codexalgerie.com</span>
        <nav aria-label={t.footer.legal} className="flex flex-wrap gap-4 md:gap-6">
          {legal.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="mono-label link-line"
              style={{ color: 'var(--faint)' }}
              data-cursor
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <span className="mono-label hidden md:block">{t.footer.designed}</span>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="mono-label flex items-center gap-2"
          style={{ color: 'var(--ink)', minHeight: 'var(--touch)' }}
        >
          {t.footer.backTop} <ArrowUp size={12} style={{ color: 'var(--accent)' }} />
        </button>
      </div>
    </footer>
  );
}
